import type { Aprovacao, Membro, Projeto, Tarefa } from "@escalando/contracts";
import { useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { Carregando, dataHora, Situacao, useDados } from "../comum";

type ProjetoVisivel = Projeto & { membro?: Membro };
export type AprovacaoVisivel = Aprovacao & { pode_decidir: boolean; project_id: string; titulo: string };

const ESPECIALISTAS = [
  ["planejamento", "Planejar (quebra em etapas)"], ["conteudo", "Conteúdo"], ["codigo", "Código"], ["imagens", "Imagens"],
  ["midia", "Mídia"], ["browser", "Pesquisa na web"], ["aws", "AWS (análise)"],
] as const;

export function ListaTarefas({ tarefas }: { tarefas: Tarefa[] }) {
  if (!tarefas.length) return <p className="suave">Nenhuma tarefa.</p>;
  return (
    <>
      {tarefas.map((t) => (
        <a key={t.task_id} href={`#/tarefa/${t.task_id}`} className="cartao" style={{ display: "block", color: "inherit" }}>
          <div className="linha" style={{ justifyContent: "space-between" }}>
            <strong>{t.title}</strong>
            <Situacao s={t.status} />
          </div>
          <div className="suave">{t.project_id} · {t.specialist} · {dataHora(t.updated_at)}</div>
        </a>
      ))}
    </>
  );
}

export function Inicio() {
  const apr = useDados(() => api<{ aprovacoes: AprovacaoVisivel[] }>("GET", "aprovacoes"));
  const tar = useDados(() => api<{ tarefas: Tarefa[] }>("GET", "tarefas?limite=10"));
  const minhas = (apr.dados?.aprovacoes ?? []).filter((a) => a.pode_decidir);
  return (
    <>
      <h1>Caixa de entrada</h1>
      <h2>Aguardando você</h2>
      {!apr.dados ? <Carregando erro={apr.erro} /> : minhas.length ? minhas.map((a) => (
        <a key={a.approval_id} href={`#/tarefa/${a.task_id}`} className="cartao" style={{ display: "block", color: "inherit" }}>
          <strong>{a.kind === "AUTORIZAR_ACAO" ? `Autorizar ação ${a.action_type ?? ""}` : "Aprovar resultado"}</strong>
          <div>{a.titulo}</div>
          <div className="suave">{a.resumo.slice(0, 160)} · expira {dataHora(a.expires_at)}</div>
        </a>
      )) : <p className="suave">Nada pendente.</p>}
      <h2>Tarefas recentes</h2>
      {tar.dados ? <ListaTarefas tarefas={tar.dados.tarefas} /> : <Carregando erro={tar.erro} />}
    </>
  );
}

export function Tarefas() {
  const proj = useDados(() => api<{ projetos: ProjetoVisivel[] }>("GET", "projetos"));
  const [filtro, setFiltro] = useState("");
  const tar = useDados(() => api<{ tarefas: Tarefa[] }>("GET", `tarefas?limite=50${filtro ? `&project_id=${filtro}` : ""}`), [filtro]);
  const [nova, setNova] = useState(false);
  return (
    <>
      <div className="linha" style={{ justifyContent: "space-between" }}>
        <h1>Tarefas</h1>
        <button className="primario" onClick={() => setNova(!nova)}>{nova ? "Fechar" : "Nova tarefa"}</button>
      </div>
      {nova && proj.dados && <NovaTarefa projetos={proj.dados.projetos} aoCriar={() => { setNova(false); tar.recarregar(); }} />}
      <select value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Projeto">
        <option value="">Todos os projetos</option>
        {proj.dados?.projetos.map((p) => <option key={p.project_id} value={p.project_id}>{p.nome}</option>)}
      </select>
      <div style={{ marginTop: 12 }}>{tar.dados ? <ListaTarefas tarefas={tar.dados.tarefas} /> : <Carregando erro={tar.erro} />}</div>
    </>
  );
}

function NovaTarefa({ projetos, aoCriar }: { projetos: ProjetoVisivel[]; aoCriar: () => void }) {
  const editaveis = projetos.filter((p) => !p.membro || p.membro.papel !== "visualizador");
  const [projeto, setProjeto] = useState(editaveis[0]?.project_id ?? "");
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [especialista, setEspecialista] = useState("planejamento");
  const [aprovar, setAprovar] = useState(false);
  const [erro, setErro] = useState<string>();
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(undefined);
    try {
      await api("POST", "tarefas", { tarefa: { project_id: projeto, title: titulo, description: descricao, specialist: especialista, aprovar_resultado: aprovar } });
      aoCriar();
    } catch (x) {
      setErro(x instanceof ErroApi ? x.message : "falha ao criar");
    }
  };
  return (
    <form className="cartao" onSubmit={enviar}>
      <label htmlFor="p">Projeto</label>
      <select id="p" value={projeto} onChange={(e) => setProjeto(e.target.value)}>{editaveis.map((p) => <option key={p.project_id} value={p.project_id}>{p.nome}</option>)}</select>
      <label htmlFor="t">O que precisa ser feito</label>
      <input id="t" required maxLength={200} value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      <label htmlFor="d">Detalhes</label>
      <textarea id="d" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
      <label htmlFor="e">Tipo</label>
      <select id="e" value={especialista} onChange={(e) => setEspecialista(e.target.value)}>{ESPECIALISTAS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}</select>
      <label className="linha"><input type="checkbox" style={{ width: "auto", minHeight: 0 }} checked={aprovar} onChange={(e) => setAprovar(e.target.checked)} /> Quero aprovar o resultado antes de concluir</label>
      {erro && <p className="erro">{erro}</p>}
      <button className="primario" type="submit">Criar tarefa</button>
    </form>
  );
}
